import { IDispatchSetStateAction } from "@Components/shared/types";
import { TermsTitles, TermsTypes } from "@Constants";
import { Button, Checkbox, Form, FormInstance, Input, Tooltip } from "antd";
import React, { useState, useContext } from "react";
import PasswordValidator from "./PasswordValidator";
import { StepInterface } from "./Registration";
import { MainContext } from "@Context/provider";
import { ViewMode } from "@Context/state";
import TermsModal from "@Components/shared/TermsModal";
import { ITerm } from "@Models/ITerms";

interface SecurityProps {
    current: number;
    steps: StepInterface[];
    totumTerms: boolean;
    updateTerms: IDispatchSetStateAction<boolean>;
    form: FormInstance;
    termsQueries: {
        [TermsTypes.Terms]: { data: ITerm; isLoading: boolean };
        [TermsTypes.PrivacyPolicy]: { data: ITerm; isLoading: boolean };
    };
}

export interface RuleDescription {
    description: string;
    checked: boolean;
}

export interface Rules {
    minChar: RuleDescription;
    lower: RuleDescription;
    upper: RuleDescription;
    number: RuleDescription;
    special: RuleDescription;
}

/**
 * Function checks if string contains any lowercase letters.
 * @param str string
 * @returns boolean
 */
export const hasLowerCase = (str) => {
    return /[a-z]/.test(str);
};

/**
 * Function checks if string contains any uppercase letters.
 * @param str string
 * @returns boolean
 */
export const hasUpperCase = (str) => {
    return /[A-Z]/.test(str);
};

/**
 * Function checks if string contains any numbers.
 * @param str string
 * @returns boolean
 */
export const hasNumber = (str) => {
    return /[0-9]/.test(str);
};

/**
 * Function checks if string contains any special characters.
 * @param str string
 * @returns boolean
 */
export const hasSpecialChar = (str) => {
    return /[^A-Za-z0-9]/.test(str);
};

const VALIDATOR_ID = "validator";

const Security = ({ current, form, steps, totumTerms, updateTerms, termsQueries }: SecurityProps) => {
    const isCurrent = current === steps.length - 1;
    const { ui: uiState } = useContext(MainContext);
    const isDesktop = uiState.mode === ViewMode.desktop;
    const [isTermsModalVisible, setTermsModalVisible] = useState(false);
    const [termsContent, setTermsContent] = useState("");
    const [currentTermsType, setCurrentTermsType] = useState("");

    const defaultRules: Rules = {
        minChar: { description: "Be at least 8 characters", checked: false },
        lower: { description: "Contain a lowercase letter", checked: false },
        upper: { description: "Contain an uppercase letter", checked: false },
        number: { description: "Contain a number", checked: false },
        special: { description: "Contain a special character", checked: false },
    };
    const [passwordRules, updateRules] = React.useState(defaultRules);

    const handleChangePassword = (event, passwordRules) => {
        const { value } = event.target;
        form.setFieldsValue({ password: value });
        form.validateFields(["password"]);

        let minChar;
        let lower;
        let upper;
        let number;
        let special;

        // check length
        if (value.length >= 8) {
            minChar = { ...passwordRules.minChar, checked: true };
        } else {
            minChar = { ...passwordRules.minChar, checked: false };
        }

        // check lowercase
        if (hasLowerCase(value)) {
            lower = { ...passwordRules.lower, checked: true };
        } else {
            lower = { ...passwordRules.lower, checked: false };
        }

        // check uppercase
        if (hasUpperCase(value)) {
            upper = { ...passwordRules.upper, checked: true };
        } else {
            upper = { ...passwordRules.upper, checked: false };
        }

        // check number
        if (hasNumber(value)) {
            number = { ...passwordRules.number, checked: true };
        } else {
            number = { ...passwordRules.number, checked: false };
        }

        // check special character
        if (hasSpecialChar(value)) {
            special = { ...passwordRules.special, checked: true };
        } else {
            special = { ...passwordRules.special, checked: false };
        }

        updateRules({ minChar, lower, upper, number, special });
    };

    const isRuleFalse = (rules: Rules) => {
        return Object.values(rules).filter((element) => {
            return element.checked === false;
        });
    };

    const handleTermsClick = (type: string) => {
        setCurrentTermsType(type);
        const termData = termsQueries[type]?.data;
        if (termData) {
            setTermsContent(termData.content);
            setTermsModalVisible(true);
        }
    };

    return (
        <div className="security-form" style={isCurrent ? null : { display: "none " }}>
            <Tooltip title="A great way to connect with other practitioners. You can change this option at any time within your account settings page">
                <Form.Item
                    label="Visible in search"
                    name="discoverability"
                    className={isDesktop ? "black-label" : ""}
                    valuePropName="checked"
                >
                    <Checkbox checked>
                        <span style={isDesktop ? { color: "black" } : null}>
                            Allow users outside my groups to find me in user search results
                        </span>
                    </Checkbox>
                </Form.Item>
            </Tooltip>
            <Form.Item
                label="Password"
                name="password"
                className={isDesktop ? "black-label" : ""}
                rules={[
                    {
                        required: true,
                        message: "Please input your password",
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            const result = isRuleFalse(passwordRules);
                            if (result.length > 0) {
                                return Promise.reject(new Error("Please ensure your password follows all criteria"));
                            }

                            return Promise.resolve(); // if (!value || getFieldValue("password") === value) {
                        },
                    }),
                ]}
            >
                <Input.Password
                    placeholder="Please input your password"
                    onChange={(e) => handleChangePassword(e, passwordRules)}
                    aria-describedby={VALIDATOR_ID}
                />
            </Form.Item>
            <PasswordValidator id={VALIDATOR_ID} passwordRules={passwordRules} />
            <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                className={isDesktop ? "black-label" : ""}
                dependencies={["password"]}
                rules={[
                    {
                        required: true,
                        message: "Please confirm your password",
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error("Please ensure that both passwords match"));
                        },
                    }),
                ]}
            >
                <Input.Password placeholder="Please confirm your password" />
            </Form.Item>
            <Form.Item
                label="Legals"
                name="termsAndConditions"
                className={isDesktop ? "terms black-label" : "terms"}
                valuePropName="checked"
                rules={[
                    {
                        required: true,
                        message: "You must agree to the terms and conditions and privacy policy",
                    },
                ]}
            >
                <Checkbox onClick={() => updateTerms(!totumTerms)}>
                    <span style={isDesktop ? { color: "black" } : null}>I agree to the </span>
                    <Button
                        type="primary"
                        onClick={() => handleTermsClick(TermsTypes.Terms)}
                        loading={termsQueries[TermsTypes.Terms]?.isLoading}
                    >
                        T&amp;C's
                    </Button>
                    <span style={isDesktop ? { color: "black" } : null}> and </span>
                    <Button
                        type="primary"
                        onClick={() => handleTermsClick(TermsTypes.PrivacyPolicy)}
                        loading={termsQueries[TermsTypes.PrivacyPolicy]?.isLoading}
                    >
                        Privacy Policy
                    </Button>
                    <TermsModal
                        visible={isTermsModalVisible}
                        onClose={() => setTermsModalVisible(false)}
                        termsContent={termsContent}
                        title={TermsTitles[currentTermsType] || "Document"}
                        termsVersionId={termsQueries[currentTermsType]?.data?.termsVersionId}
                        showFooter={false}
                    />
                </Checkbox>
            </Form.Item>
        </div>
    );
};

export default Security;
